/**
 * Shapes for the Topic Cluster Builder (/tools/topic-cluster-builder).
 *
 * Everything persisted on `tools.tool_runs` for this tool is declared here:
 * `input`, `facts` (the expansion and the optional site context), `findings`
 * (counts the report and Slack read) and `writeup` (Claude's validated
 * matrix). Pure types only.
 */
import type { RunDto, RunRow } from '@/lib/tool-runs/types'

export type TopicClusterInput = {
  /** As typed, trimmed and collapsed. */
  seed: string
  /** A page on the site the plan is for, or null. */
  siteUrl: string | null
  /** Free text, or null. */
  industry: string | null
}

/** What the Google Autocomplete expansion produced. Best effort; never fails a run. */
export type Expansion = {
  /** Lower-cased, deduped, at most EXPANSION_CAP. */
  suggestions: string[]
  /** How many suggest requests were made. */
  requested: number
  /** How many answered 200 with a parseable body. */
  succeeded: number
  /** True when every request failed, so nothing is grounded. */
  blocked: boolean
}

export type SiteContext = {
  url: string
  title: string | null
  description: string | null
  h1: string | null
  navAnchors: string[]
  schemaTypes: string[]
}

export type TopicClusterFacts = {
  version: 1
  seed: string
  industry: string | null
  expansion: Expansion
  /** null when no site URL was given or the page could not be fetched. */
  site: SiteContext | null
  collectedAt: string
}

/** The `findings` jsonb column: the deterministic counts. */
export type TopicClusterFindings = {
  suggestionCount: number
  blocked: boolean
  /** Subtopics whose keyword appeared verbatim in the live suggestions. */
  groundedCount: number
  /** Subtopics the model proposed on its own. */
  modelCount: number
  clusterCount: number
  subtopicCount: number
}

export type Intent = 'informational' | 'commercial' | 'transactional' | 'navigational'
export type FunnelStage = 'awareness' | 'consideration' | 'decision' | 'retention'
export type SubtopicSource = 'autocomplete' | 'model'

export type Subtopic = {
  keyword: string
  intent: Intent
  funnel_stage: FunnelStage
  content_type: string
  source: SubtopicSource
  note: string
}

export type Cluster = {
  name: string
  intent: Intent
  funnel_stage: FunnelStage
  content_type: string
  subtopics: Subtopic[]
}

export type TopicClusterWriteup = {
  /** Whether the seed is the hub of its own topic or one spoke of a larger one. */
  seed_role: 'pillar' | 'subtopic'
  /** The larger topic the seed belongs to; required when seed_role is subtopic. */
  parent_pillar: string | null
  pillar: { name: string; angle: string }
  clusters: Cluster[]
  caveats: string[]
  suggested_next_step: string
}

export type TopicClusterRow = RunRow<
  TopicClusterInput,
  TopicClusterFacts,
  TopicClusterFindings,
  TopicClusterWriteup
>
export type TopicClusterDto = RunDto<
  TopicClusterInput,
  TopicClusterFacts,
  TopicClusterFindings,
  TopicClusterWriteup
>

export const TOPIC_CLUSTER_STAGES = ['expand', 'write'] as const

export const SEED_MIN = 2
export const SEED_MAX = 80
export const INDUSTRY_MAX = 80
export const EXPANSION_CAP = 120

export const INTENTS = ['informational', 'commercial', 'transactional', 'navigational'] as const
export const FUNNEL_STAGES = ['awareness', 'consideration', 'decision', 'retention'] as const
